// Replace one Therapist of a paid booking — all of her Clients at once
// (THER-040 / SCHED-025, migration 0230, plan S4/S6).
//
// ⚠ Superadmin-only, and not by preference: who may replace a locked Therapist
// is an Open Item. The server refuses every other role; the card that opens
// this dialog does not offer it to them either.

import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Spinner } from "@bednbody/ui/components/spinner";
import { Textarea } from "@bednbody/ui/components/textarea";
import { cn } from "@bednbody/ui/lib/utils";
import { WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { EdgeFunctionError } from "@/lib/edge-functions";
import { useHubTherapists, useReplaceTherapistEntry } from "./booking-entries.hooks";

/** The engine's blocker codes, in words an operator can act on. */
const BLOCKER_LABEL: Record<string, string> = {
  STAFF_INACTIVE: "is inactive",
  SCHEDULE_UNPUBLISHED: "has no published schedule that day",
  OFF_DUTY: "is not on duty for the whole window",
  CONFLICTING_COMMITMENT: "is already booked in that window",
  UNQUALIFIED: "is not qualified for every service in this booking",
  SERVICE_AREA_INELIGIBLE: "is outside the service area",
};

function refusalMessage(error: unknown): string {
  if (!(error instanceof EdgeFunctionError)) {
    return error instanceof Error ? error.message : "The replacement failed.";
  }
  const blockers = (error.details as { blockers?: string[] } | undefined)?.blockers ?? [];
  if (blockers.length > 0) {
    return `She ${blockers.map((code) => BLOCKER_LABEL[code] ?? code).join(", and ")}.`;
  }
  return error.message;
}

export function ReplaceTherapistDialog({
  open,
  onOpenChange,
  bookingGroupId,
  hubId,
  fromTherapistId,
  fromTherapistName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingGroupId: string;
  hubId: string | null;
  fromTherapistId: string;
  fromTherapistName: string;
}) {
  const [therapistId, setTherapistId] = useState("");
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const candidates = useHubTherapists(open ? hubId : null, fromTherapistId);
  const replace = useReplaceTherapistEntry(bookingGroupId);

  const trimmedReason = reason.trim();
  const canSubmit = therapistId !== "" && trimmedReason.length > 0 && !replace.isPending;
  const options = candidates.data ?? [];

  function close() {
    setTherapistId("");
    setReason("");
    setErrorMessage(null);
    onOpenChange(false);
  }

  function submit() {
    setErrorMessage(null);
    replace.mutate(
      { fromTherapistId, toTherapistId: therapistId, reason: trimmedReason },
      {
        onSuccess: () => {
          toast.success("Therapist replaced");
          close();
        },
        onError: (error) => setErrorMessage(refusalMessage(error)),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Replace {fromTherapistName}</DialogTitle>
          <DialogDescription>
            The new Therapist takes exactly her Clients and services — nothing is rebalanced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <fieldset>
            <legend className="mb-2 block text-sm font-medium">Replace with</legend>
            {candidates.isLoading ? (
              <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
                <Spinner className="size-4" />
                Loading this hub&rsquo;s Therapists…
              </div>
            ) : options.length === 0 ? (
              <p className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
                No other active Therapist belongs to this hub.
              </p>
            ) : (
              <RadioGroup value={therapistId} onValueChange={setTherapistId}>
                <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                  {options.map((t) => (
                    <label
                      key={t.id}
                      htmlFor={`replace-therapist-${t.id}`}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors",
                        therapistId === t.id ? "border-primary bg-primary/5" : "border-border",
                      )}
                    >
                      <RadioGroupItem id={`replace-therapist-${t.id}`} value={t.id} />
                      <span className="min-w-0 flex-1 truncate text-sm">{t.fullName}</span>
                    </label>
                  ))}
                </div>
              </RadioGroup>
            )}
          </fieldset>

          <div className="space-y-2">
            <label htmlFor="replace-therapist-reason" className="block text-sm font-medium">
              Reason (required)
            </label>
            <Textarea
              id="replace-therapist-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. called in sick; cover arranged."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Written to the audit log with your name.
            </p>
          </div>

          <p className="flex gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
            <WarningIcon weight="fill" className="mt-px size-4 shrink-0" />
            <span>
              Superadmin-only while the authority is an open item. The server re-checks duty,
              qualification and her other bookings, and only replaces work that has not started.
            </span>
          </p>

          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={replace.isPending}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {replace.isPending ? "Replacing…" : "Replace therapist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
